import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

interface ScrollToTopProps {
  behavior?: 'auto' | 'smooth';
  delay?: number;
  excludePaths?: string[];
}

export const ScrollToTop: React.FC<ScrollToTopProps> = ({
  behavior = 'smooth',
  delay = 0,
  excludePaths = []
}) => {
  const { pathname } = useLocation();
  const prevPathnameRef = useRef<string>('');

  useEffect(() => {
    // 检查路径是否真的发生了变化
    if (prevPathnameRef.current === pathname) {
      return;
    }

    // 检查是否在排除路径列表中
    const shouldExclude = excludePaths.some(excludePath =>
      pathname.includes(excludePath)
    );

    if (shouldExclude) {
      prevPathnameRef.current = pathname;
      return;
    }

    // 滚动到页面顶部的函数
    const scrollToTop = () => {
      // 检查是否需要滚动（如果已经在顶部就不需要滚动）
      if (window.pageYOffset === 0) {
        return;
      }

      window.scrollTo({
        top: 0,
        left: 0,
        behavior
      });
    };

    // 如果设置了延迟，则延迟执行
    if (delay > 0) {
      const timeoutId = setTimeout(scrollToTop, delay);
      prevPathnameRef.current = pathname;

      return () => clearTimeout(timeoutId);
    } else {
      // 使用 requestAnimationFrame 确保在下一帧执行，避免与路由动画冲突
      const rafId = requestAnimationFrame(scrollToTop);
      prevPathnameRef.current = pathname;

      return () => cancelAnimationFrame(rafId);
    }
  }, [pathname, behavior, delay, excludePaths]);

  return null; // 这个组件不渲染任何内容
};
